import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';
import { TankBalance } from './tanks';

// ============================================================================
// Types
// ============================================================================

// Re-export enums from inventory queries for convenience
export { ActionDirection, ShipmentType, entryStatus, entrySource } from '@api/queries/inventory';
export { EntryType } from '~types/types';

export interface TruckCarrier {
  driverName?: string;
  licensePlate?: string;
  waybillNumber?: string;
  transportationCompanyId?: string;
  transportationCompanyName?: string;
}

export interface ShipCarrier {
  shipName?: string;
  billOfLadingNo?: string;
}

export interface TrainCarrier {
  wagonNumber?: string;
}

export interface PipeCarrier {
  pipeNumber?: string;
}

export interface ContainerTrainCarrier {
  containerNumber?: string;
  wagonNumber?: string;
}

export interface ContainerTruckCarrier {
  containerNumber?: string;
  licensePlate?: string;
}

export interface ExternalData {
  id: string;
  type: string;
  data: string;
}

export interface ProductMixtureEntry {
  productId: string;
  productName: string;
  productCode: string;
  percentage: number;
}

export interface BookkeepingEntry {
  id: string;
  created?: string;
  createdBy: string;
  modified?: string;
  modifiedBy: string;
  approved?: string;
  approvedBy: string;
  actionDirection: number; // ActionDirection enum
  terminalId?: string;
  terminalName?: string;
  tankId?: string;
  tankName?: string;
  customerId?: string;
  customerName?: string;
  productId?: string;
  productAliasId?: string;
  productName?: string;
  productCode?: string;
  productMixtureId: string;
  shipmentType: number; // ShipmentType enum
  entryType: number; // EntryType enum
  shipmentTypeName: string;
  unloadingStarted: string;
  unloadingFinished: string;
  unloadingStartedTimeSelected: boolean;
  unloadingFinishedTimeSelected: boolean;
  amount?: number;
  truck: TruckCarrier;
  ship: ShipCarrier;
  train: TrainCarrier;
  pipe: PipeCarrier;
  containerTrain: ContainerTrainCarrier;
  containerTruck: ContainerTruckCarrier;
  notes?: string;
  entryState: number; // entryStatus enum
  source: number; // entrySource enum
  externalData?: ExternalData[];
  billingInformation?: string;
  order: string;
  externalDataId?: string;
  measurementId?: string;
  flowMeterData?: ExternalData[];
  flowMeterComponentId?: string;
  hydrogenPeroxideFlowMeterData?: ExternalData[];
  hydrogenPeroxideFlowMeterId?: string;
  concentrationPercent: number;
  mixtures?: ProductMixtureEntry[];
  sendToCustomer?: boolean;
  lastSentToCustomer?: string;
}

export interface ApprovalEntry {
  isApprovable: boolean;
  balance: number;
  entry: BookkeepingEntry;
}

export interface TankApproval {
  tankId: string;
  approvedBalance: TankBalance;
  entries: ApprovalEntry[];
}

export interface ApprovalData {
  tankId: string;
  approvedBalance: TankBalance;
  entries: BookkeepingEntry[];
  approvable: Record<string, boolean>;
  balances: Record<string, number>;
}

interface ApproveCmd {
  until?: string;
  useAdminOverride: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'bookkeepingapproval';

const fetchApproval = async (tankId: string, useAdminOverride: boolean): Promise<TankApproval> => {
  return apiRequest<void, TankApproval>(
    HttpMethod.GET,
    `${API_ENDPOINT}?tankId=${tankId}&useAdminOverride=${useAdminOverride}`
  );
};

const approveEntries = async (tankId: string, cmd: ApproveCmd): Promise<TankApproval> => {
  return apiRequest<ApproveCmd, TankApproval>(HttpMethod.POST, `${API_ENDPOINT}?tankId=${tankId}`, cmd);
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transform API response to a more usable format for the UI
 */
const transformApprovalData = (result: TankApproval): ApprovalData => {
  const approvable: Record<string, boolean> = {};
  const balances: Record<string, number> = {};
  const entries: BookkeepingEntry[] = [];

  result.entries.forEach(e => {
    approvable[e.entry.id] = e.isApprovable;
    balances[e.entry.id] = e.balance;
    entries.push(e.entry);
  });

  return {
    tankId: result.tankId,
    approvedBalance: result.approvedBalance,
    entries,
    approvable,
    balances,
  };
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch approval data for a specific tank
 */
export const useApprovalQuery = (tankId: string | undefined, useAdminOverride: boolean) => {
  return useQuery({
    queryKey: queryKeys.approval.unapproved(),
    queryFn: async () => {
      const result = await fetchApproval(tankId!, useAdminOverride);
      return transformApprovalData(result);
    },
    enabled: !!tankId,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Approve bookkeeping entries up to a specific order
 */
export const useApproveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tankId, until, useAdminOverride }: { tankId: string; until?: string; useAdminOverride: boolean }) =>
      approveEntries(tankId, { until, useAdminOverride }),
    onSuccess: (result) => {
      // Update the approval query with the new data
      queryClient.setQueryData(queryKeys.approval.unapproved(), transformApprovalData(result));
      // Also invalidate tank balances as approval affects them
      queryClient.invalidateQueries({ queryKey: queryKeys.tanks.balances() });
      toastService.success({ title: 'approval.approved_succesfully' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
